import React, { useState, useContext, useMemo, useEffect } from 'react';
import { AppContext } from '../context/AppContext';
import { 
  X, 
  FileText, 
  Users, 
  UserCheck, 
  DollarSign, 
  Calendar, 
  Plus, 
  Trash2, 
  Send, 
  Sparkles, 
  Building2, 
  Share2, 
  Copy, 
  Check, 
  ShieldCheck, 
  BookmarkCheck,
  Percent
} from 'lucide-react';

const PRESET_CLAUSES = [
  { title: '수습기간 및 급여 규정', content: '입사일로부터 3개월간 수습기간을 적용하며, 본 기간 중 급여의 100%를 정상 지급한다.' },
  { title: '스톡옵션(주식매수선택권) 부여', content: '입사 1년 근속 후 회사 이사회 결의에 따라 주식매수선택권(스톡옵션) 1,000주를 부여받을 수 있다.' },
  { title: '법인 장비 및 IT 자산 지급', content: '업무용 고성능 랩탑 및 모니터 장비를 지원하며, 퇴사 시 즉시 원상태로 반납하여야 한다.' },
  { title: '스마트워크 & 재택근무 규정', content: '주 2회 부서장 사전 승인 하에 원격/재택근무를 실시할 수 있으며 보안 수칙을 엄수한다.' },
  { title: '산출물 지식재산권(IP) 귀속', content: '용역 대금 전액 지급 완료 시 본 과업을 통해 제작된 모든 산출물의 저작권은 회사에 완전 귀속된다.' },
  { title: '사직의사 통보 및 효력', content: '본인은 자유의사로 사직원을 제출하며, 회사의 수리 또는 민법 제660조에 따른 기간 경과 시 퇴직 효력이 발생함을 확인합니다.' },
  { title: '퇴사자 사내 자산 및 데이터 완전 반납', content: '노트북, 출입카드, 법인카드, 업무 관련 모든 문서 및 백업 파일을 퇴사일까지 부서장에게 완전 반납합니다.' },
  { title: '퇴사 후 영업비밀 유지 및 경업금지', content: '재직 중 취득한 핵심 기술 및 영업비밀을 퇴사 후에도 제3자에게 유출하지 않으며 동종업계 영업비밀 침해를 금합니다.' },
  { title: '업무 및 계정 인수인계 완료', content: '담당 직무 및 진행 중인 모든 프로젝트의 인수인계서를 작성하여 후임자에게 이상 없이 인계 완료하였습니다.' }
];

const PRESET_SALARY_ITEMS = [
  { name: '식대', defaultAmount: 200000, isTaxFree: true, note: '비과세 (월 20만원 한도)' },
  { name: '차량유지보조금', defaultAmount: 200000, isTaxFree: true, note: '비과세 (자가운전보조금)' },
  { name: '육아수당', defaultAmount: 200000, isTaxFree: true, note: '비과세 (6세 이하 자녀)' },
  { name: '직책수당', defaultAmount: 300000, isTaxFree: false, note: '과세' },
  { name: '연구개발수당', defaultAmount: 200000, isTaxFree: true, note: '비과세 (연구전담)' },
  { name: '기타 제수당', defaultAmount: 100000, isTaxFree: false, note: '과세' }
];

const ContractCreateModal = ({ isOpen, onClose, onCreatedSuccess }) => {
  const { users, contractTemplates, createContract, saveContractTemplate, companySeal } = useContext(AppContext);

  const [targetType, setTargetType] = useState('EMPLOYEE'); // 'EMPLOYEE' | 'FREELANCER'
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [department, setDepartment] = useState('');
  const [position, setPosition] = useState('');
  const [residentNumber, setResidentNumber] = useState('');
  const [userAddress, setUserAddress] = useState('');

  const getInitialStandardClauses = (type, target = targetType) => {
    const isFreelance = type === 'FREELANCE' || target === 'FREELANCER';
    const isNdaType = type === 'NDA' || type === 'RESIGNATION_NDA';
    const targetName = isFreelance ? '수급인' : (isNdaType ? '서약인' : '근로자');
    
    return [
      { title: '제 1 조 (신의성실 및 법령 준수)', content: '양 당사자는 본 계약에서 정한 권리와 의무를 신의에 따라 성실히 이행하며, 관련 법령 및 사내 제반 규정을 엄격히 준수한다.' },
      { title: '제 2 조 (성실의무 및 겸업 금지)', content: `${targetName}은(는) 본 과업 수행 중 회사의 명예를 훼손하거나 업무에 지장을 초래하는 행위를 하여서는 아니 되며, 회사의 사전 승낙 없는 겸업을 금지한다.` },
      { title: '제 3 조 (비밀유지 및 지식재산권)', content: '본 계약 및 과업 수행 중 취득한 회사의 영업비밀, 고객 정보, 시스템 소스코드 등 일체의 기밀 정보는 계약 종료 후에도 3년간 외부에 누설하지 아니하며, 산출물의 지식재산권은 회사에 완전 귀속된다.' }
    ];
  };

  const [contractType, setContractType] = useState('LABOR'); // 'LABOR' | 'SALARY' | 'FREELANCE' | 'NDA' | 'CUSTOM'
  const [standardClauses, setStandardClauses] = useState(() => getInitialStandardClauses('LABOR', 'EMPLOYEE'));
  const [title, setTitle] = useState('2026년도 표준 근로계약서');
  const [contractStartDate, setContractStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [contractEndDate, setContractEndDate] = useState('');
  
  // Link Expiration Policy (Default: 7_DAYS)
  const [expireOption, setExpireOption] = useState('7_DAYS'); // '7_DAYS' | '14_DAYS' | '30_DAYS' | 'CUSTOM' | 'UNLIMITED'
  const [customExpireDate, setCustomExpireDate] = useState('');
  
  // Regular Employee Salary & Compensation
  const [annualSalary, setAnnualSalary] = useState(50000000);
  const [salaryPayday, setSalaryPayday] = useState('매달 말일');
  const [salaryItems, setSalaryItems] = useState([
    { id: 'base', name: '기본급', amount: 3967000, isTaxFree: false, note: '기본급여' },
    { id: 'meal', name: '식대', amount: 200000, isTaxFree: true, note: '비과세 (월 20만원 한도)' }
  ]);

  // Freelance Compensation
  const [freelancePaymentDate, setFreelancePaymentDate] = useState('매월 10일 (과업 검수 완료 후 7영업일 이내)');
  const [freelanceTaxMode, setFreelanceTaxMode] = useState('INCOME_TAX_3_3'); // 'INCOME_TAX_3_3' | 'VAT_10' | 'TAX_EXEMPT'
  const [freelanceItems, setFreelanceItems] = useState([
    { id: 'f_1', name: '용역 대금 (공급가액)', amount: 5000000, note: '본 과업 일체 수행비' }
  ]);

  const [workingHours, setWorkingHours] = useState('09:00 ~ 18:00 (휴게 12:00 ~ 13:00)');
  const [workingPlace, setWorkingPlace] = useState(() => {
    return companySeal?.address || '서울시 강서구 양천로 354 옥천빌딩 9층 클로우플레이크 본사 및 재택';
  });
  const [jobDescription, setJobDescription] = useState('클라우드 웹메일 포털 프론트엔드 및 AI 어시스턴트 서비스 개발');

  // Synchronize workingPlace with companySeal.address on modal open
  useEffect(() => {
    if (isOpen && companySeal?.address) {
      setWorkingPlace(companySeal.address);
    }
  }, [isOpen, companySeal]);

  // Dynamic totals for Regular Employee Salary
  const monthlySalaryTotal = useMemo(() => {
    return salaryItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [salaryItems]);

  const totalTaxFree = useMemo(() => {
    return salaryItems.filter(item => item.isTaxFree).reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [salaryItems]);

  const totalTaxable = useMemo(() => {
    return Math.max(0, monthlySalaryTotal - totalTaxFree);
  }, [monthlySalaryTotal, totalTaxFree]);

  // Dynamic totals for Freelance
  const freelanceTotalSupply = useMemo(() => {
    return freelanceItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [freelanceItems]);

  const freelanceTax = useMemo(() => {
    if (freelanceTaxMode === 'INCOME_TAX_3_3') {
      return Math.floor(freelanceTotalSupply * 0.033);
    }
    if (freelanceTaxMode === 'VAT_10') {
      return Math.floor(freelanceTotalSupply * 0.1);
    }
    return 0;
  }, [freelanceTotalSupply, freelanceTaxMode]);

  const freelanceNetOrTotal = useMemo(() => {
    if (freelanceTaxMode === 'INCOME_TAX_3_3') {
      return freelanceTotalSupply - freelanceTax;
    }
    if (freelanceTaxMode === 'VAT_10') {
      return freelanceTotalSupply + freelanceTax;
    }
    return freelanceTotalSupply;
  }, [freelanceTotalSupply, freelanceTax, freelanceTaxMode]);

  // Handle Annual Salary Input Change -> Recalculates Base Pay
  const handleAnnualSalaryChange = (newVal) => {
    const num = Number(newVal) || 0;
    setAnnualSalary(num);
    const targetMonthly = Math.floor(num / 12);
    const nonBaseSum = salaryItems.filter(i => i.id !== 'base' && i.name !== '기본급').reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const newBase = Math.max(0, targetMonthly - nonBaseSum);

    setSalaryItems(prev => {
      const hasBase = prev.some(i => i.id === 'base' || i.name === '기본급');
      if (hasBase) {
        return prev.map(i => (i.id === 'base' || i.name === '기본급') ? { ...i, amount: newBase } : i);
      } else {
        return [{ id: 'base', name: '기본급', amount: newBase, isTaxFree: false, note: '기본급여' }, ...prev];
      }
    });
  };

  // Salary item modifiers
  const handleAddSalaryPreset = (preset) => {
    const newItem = {
      id: `salary_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      name: preset.name,
      amount: preset.defaultAmount,
      isTaxFree: preset.isTaxFree,
      note: preset.note
    };
    const nextItems = [...salaryItems, newItem];
    setSalaryItems(nextItems);
    const nextMonthly = nextItems.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    setAnnualSalary(nextMonthly * 12);
  };

  const handleAddCustomSalaryItem = () => {
    const newItem = {
      id: `salary_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      name: '새 수당 항목',
      amount: 100000,
      isTaxFree: false,
      note: ''
    };
    const nextItems = [...salaryItems, newItem];
    setSalaryItems(nextItems);
    const nextMonthly = nextItems.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    setAnnualSalary(nextMonthly * 12);
  };

  const handleUpdateSalaryItem = (idx, field, value) => {
    setSalaryItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'amount') {
        const nextMonthly = next.reduce((s, i) => s + (Number(i.amount) || 0), 0);
        setAnnualSalary(nextMonthly * 12);
      }
      return next;
    });
  };

  const handleRemoveSalaryItem = (idx) => {
    setSalaryItems(prev => {
      const next = prev.filter((_, i) => i !== idx);
      const nextMonthly = next.reduce((s, i) => s + (Number(i.amount) || 0), 0);
      setAnnualSalary(nextMonthly * 12);
      return next;
    });
  };

  // Freelance item modifiers
  const handleAddFreelanceItem = () => {
    setFreelanceItems(prev => [
      ...prev,
      { id: `f_${Date.now()}`, name: `추가 용역/과업 항목 ${prev.length + 1}`, amount: 1000000, note: '' }
    ]);
  };

  const handleUpdateFreelanceItem = (idx, field, value) => {
    setFreelanceItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleRemoveFreelanceItem = (idx) => {
    setFreelanceItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Custom Clauses
  const [customClauses, setCustomClauses] = useState([
    { title: '수습기간 특약', content: '입사일로부터 3개월간 수습기간을 적용하며, 본 기간 중 급여의 100%를 정상 지급한다.' },
    { title: '영업비밀 및 보안 준수', content: '회사에서 부여받은 시스템 계정 정보 및 인프라 키는 재직 중 및 퇴사 후에도 기밀을 유지한다.' }
  ]);

  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Post-creation share modal state
  const [createdContract, setCreatedContract] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  // Handle Employee selection change
  const handleEmployeeChange = (e) => {
    const uId = e.target.value;
    setSelectedUserId(uId);
    const emp = users.find(u => u.UserID === uId);
    if (emp) {
      setUserName(emp.Name || '');
      setUserEmail(emp.Email || '');
      setUserPhone(emp.Phone || '010-1234-5678');
      setDepartment(emp.Department || '');
      setPosition(emp.Position || '');
      setResidentNumber(emp.ResidentRegistrationNumber || emp.residentNumber || '');
      setUserAddress(emp.UserAddress || emp.userAddress || emp.Address || '');
    } else {
      setUserName('');
      setUserEmail('');
      setUserPhone('');
      setDepartment('');
      setPosition('');
      setResidentNumber('');
      setUserAddress('');
    }
  };

  const handleTargetTypeChange = (target) => {
    setTargetType(target);
    setStandardClauses(getInitialStandardClauses(contractType, target));
  };

  // Switch template
  const handleTemplateSelect = (tmpl) => {
    setContractType(tmpl.Type);
    setTitle(tmpl.DefaultTitle || tmpl.Name);
    if (Array.isArray(tmpl.DefaultClauses)) {
      setCustomClauses(tmpl.DefaultClauses.map(c => ({ ...c })));
    }
    if (Array.isArray(tmpl.DefaultStandardClauses) && tmpl.DefaultStandardClauses.length > 0) {
      setStandardClauses(tmpl.DefaultStandardClauses.map(c => ({ ...c })));
    } else {
      setStandardClauses(getInitialStandardClauses(tmpl.Type));
    }
  };

  // Add custom clause block
  const handleAddPresetClause = (preset) => {
    setCustomClauses(prev => [...prev, { ...preset }]);
  };

  const handleAddEmptyClause = () => {
    setCustomClauses(prev => [...prev, { title: '새 특약 조항', content: '상세 내용을 입력하세요.' }]);
  };

  const handleUpdateClause = (index, field, value) => {
    setCustomClauses(prev => {
      const next = [...prev];
      next[index][field] = value;
      return next;
    });
  };

  const handleRemoveClause = (index) => {
    setCustomClauses(prev => prev.filter((_, i) => i !== index));
  };

  // Determine whether salary/amount input is required
  const [includeCustomSalary, setIncludeCustomSalary] = useState(false);
  const isCompensationRequired = useMemo(() => {
    if (contractType === 'NDA' || contractType === 'PRIVACY' || contractType === 'RESIGNATION' || contractType === 'RESIGNATION_NDA' || contractType === 'HANDOVER') return false;
    if (contractType === 'LABOR' || contractType === 'SALARY' || contractType === 'FREELANCE') return true;
    if (contractType === 'CUSTOM') return includeCustomSalary;
    return false;
  }, [contractType, includeCustomSalary]);

  // Submit contract
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userName.trim()) {
      alert('계약 대상자 성명을 입력해 주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      const contractPayload = {
        TargetType: targetType,
        UserID: targetType === 'EMPLOYEE' ? selectedUserId : null,
        UserName: userName.trim(),
        UserEmail: userEmail.trim(),
        UserPhone: userPhone.trim(),
        ResidentRegistrationNumber: residentNumber.trim(),
        UserAddress: userAddress.trim(),
        Department: department.trim() || (targetType === 'FREELANCER' ? '외부 용역' : '미지정'),
        Position: position.trim() || (targetType === 'FREELANCER' ? '전문 프리랜서' : '담당'),
        ContractType: contractType,
        Title: title.trim(),
        ContractStartDate: contractStartDate,
        ContractEndDate: contractEndDate,
        
        // Regular Employee Compensation Details
        AnnualSalary: (isCompensationRequired && targetType === 'EMPLOYEE') ? Number(annualSalary) : 0,
        MonthlySalary: (isCompensationRequired && targetType === 'EMPLOYEE') ? Number(monthlySalaryTotal) : 0,
        SalaryItems: (isCompensationRequired && targetType === 'EMPLOYEE') ? salaryItems : [],
        SalaryPayday: salaryPayday,
        TotalTaxFree: (isCompensationRequired && targetType === 'EMPLOYEE') ? totalTaxFree : 0,
        TotalTaxable: (isCompensationRequired && targetType === 'EMPLOYEE') ? totalTaxable : 0,

        // Freelancer Compensation Details
        TotalAmount: isCompensationRequired ? (targetType === 'FREELANCER' ? Number(freelanceTotalSupply) : Number(annualSalary)) : 0,
        TaxAmount: (isCompensationRequired && targetType === 'FREELANCER') ? freelanceTax : 0,
        NetAmount: isCompensationRequired ? (targetType === 'FREELANCER' ? freelanceNetOrTotal : Number(monthlySalaryTotal)) : 0,
        FreelanceItems: (isCompensationRequired && targetType === 'FREELANCER') ? freelanceItems : [],
        FreelancePaymentDate: freelancePaymentDate,
        FreelanceTaxMode: freelanceTaxMode,

        WorkingHours: workingHours,
        WorkingPlace: workingPlace,
        JobDescription: jobDescription,
        CustomClauses: customClauses,
        StandardClauses: standardClauses,

        // Link Expiration Policy
        ExpireOption: expireOption,
        CustomExpireDate: customExpireDate
      };

      const result = await createContract(contractPayload);

      // Save as reusable template if checked
      if (saveAsTemplate && newTemplateName.trim()) {
        saveContractTemplate({
          Name: newTemplateName.trim(),
          Type: contractType,
          DefaultTitle: title.trim(),
          DefaultClauses: customClauses,
          DefaultStandardClauses: standardClauses
        });
      }

      setCreatedContract(result);
      if (onCreatedSuccess) onCreatedSuccess(result);
    } catch (err) {
      console.error('Failed to create contract:', err);
      alert('계약서 생성 및 발송 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const shareUrl = createdContract ? `${window.location.origin}/#/contract/sign?token=${createdContract.PublicToken}` : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
              <FileText className="w-5 h-5" />
            </div>
            <div className="text-left">
              <h2 className="text-base font-bold">새 전자계약서 작성 및 발송</h2>
              <p className="text-xs text-slate-400">사내 임직원 및 외부 프리랜서 맞춤형 계약서를 작성하고 발송합니다.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* If successfully created, show Share / KakaoTalk Screen */}
        {createdContract ? (
          <div className="p-6 md:p-8 space-y-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-xs">
              <Check className="w-8 h-8 stroke-[3]" />
            </div>

            <div className="space-y-1">
              <h3 className="text-lg font-black text-slate-900">전자계약서가 성공적으로 발송되었습니다!</h3>
              <p className="text-xs text-slate-500">
                수신자에게 서명 요청 메일이 자동 발송되었으며, 카카오톡 또는 SMS로 직접 링크를 전달할 수도 있습니다.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl text-left space-y-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">계약서 제목:</span>
                <span className="font-bold text-slate-900">{createdContract.Title}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">계약 대상자:</span>
                <span className="font-bold text-indigo-700">{createdContract.UserName} ({createdContract.UserEmail || createdContract.UserPhone})</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-bold">열람 정책:</span>
                <span className="font-bold text-emerald-700">
                  {createdContract.TargetType === 'FREELANCER' ? '서명 후 15일간 열람 보장 (보안 링크)' : '365일 24시간 상시 영구 열람'}
                </span>
              </div>
            </div>

            {/* Link Copy Box */}
            <div className="space-y-2 text-left">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5 text-indigo-600" />
                카카오톡 / 모바일 전용 서명 링크:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 text-xs bg-slate-100 border border-slate-300 rounded-xl px-3 py-2.5 font-mono text-slate-700"
                />
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all shadow-xs ${
                    isCopied ? 'bg-emerald-600' : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {isCopied ? '복사됨!' : '링크 복사'}
                </button>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-md transition-all"
              >
                확인 및 닫기
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 text-left">
            {/* Step 1: Target Type Switch */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-700 block">1. 계약 대상자 유형 선택</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setTargetType('EMPLOYEE');
                    setContractType('LABOR');
                    setTitle('2026년도 표준 정규직 근로계약서');
                    setStandardClauses(getInitialStandardClauses('LABOR', 'EMPLOYEE'));
                  }}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border text-xs font-bold transition-all ${
                    targetType === 'EMPLOYEE'
                      ? 'border-indigo-500 bg-indigo-50/60 text-indigo-800 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Users className="w-5 h-5 text-indigo-600" />
                  <div className="text-left">
                    <div className="font-black">사내 임직원 계약</div>
                    <div className="text-[10px] text-slate-500 font-normal mt-0.5">인적사항 자동 매핑 + 상시 영구 보관</div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setTargetType('FREELANCER');
                    setContractType('FREELANCE');
                    setTitle('전문 용역 도급계약서 (원천징수 3.3% 적용)');
                    setSelectedUserId('');
                    setUserName('');
                    setUserEmail('');
                    setUserPhone('');
                    setDepartment('외부 협력/개발');
                    setPosition('전문 프리랜서');
                    setStandardClauses(getInitialStandardClauses('FREELANCE', 'FREELANCER'));
                  }}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border text-xs font-bold transition-all ${
                    targetType === 'FREELANCER'
                      ? 'border-indigo-500 bg-indigo-50/60 text-indigo-800 ring-2 ring-indigo-500/20'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Building2 className="w-5 h-5 text-indigo-600" />
                  <div className="text-left">
                    <div className="font-black">외부 프리랜서 / 용역 계약</div>
                    <div className="text-[10px] text-slate-500 font-normal mt-0.5">카카오톡 URL 발송 + 15일간 열람</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Step 2: Employee or Freelancer Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-4">
              <span className="text-xs font-bold text-slate-800 block border-b border-slate-200 pb-2">
                2. 계약 대상자 정보 입력
              </span>

              {targetType === 'EMPLOYEE' ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">임직원 선택:</label>
                    <select
                      value={selectedUserId}
                      onChange={handleEmployeeChange}
                      required
                      className="w-full text-xs font-bold px-3 py-2.5 rounded-xl border border-slate-300 bg-white"
                    >
                      <option value="">-- 계약서를 발행할 직원을 선택하세요 --</option>
                      {users.map(u => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.Name} ({u.Department} {u.Position} / {u.Email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-[10px] text-slate-400 block">성명</span>
                      <input type="text" readOnly value={userName} className="w-full bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200 font-bold" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block">부서</span>
                      <input type="text" readOnly value={department} className="w-full bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block">직급</span>
                      <input type="text" readOnly value={position} className="w-full bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200" />
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-400 block">이메일</span>
                      <input type="text" readOnly value={userEmail} className="w-full bg-slate-100 px-2.5 py-1.5 rounded-lg border border-slate-200 font-mono" />
                    </div>
                  </div>

                  {/* Resident Number & Address */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-slate-200/60 text-xs">
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        주민등록번호 (직원이 서명 시 직접 작성/수정 가능)
                      </label>
                      <input
                        type="text"
                        maxLength={14}
                        value={residentNumber}
                        onChange={e => {
                          const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
                          setResidentNumber(raw.length <= 6 ? raw : `${raw.slice(0, 6)}-${raw.slice(6)}`);
                        }}
                        placeholder="예: 950101-1234567 (비워두면 직원이 서명 시 직접 입력)"
                        className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 font-mono font-bold text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 block mb-1">
                        자택 주소 (실거주지)
                      </label>
                      <input
                        type="text"
                        value={userAddress}
                        onChange={e => setUserAddress(e.target.value)}
                        placeholder="예: 서울시 강서구 양천로 354 옥천빌딩 (비워두면 직원이 서명 시 직접 입력)"
                        className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">프리랜서 성명 / 상호 *</label>
                    <input
                      type="text"
                      required
                      value={userName}
                      onChange={e => setUserName(e.target.value)}
                      placeholder="예: 박프리"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">휴대폰 번호 (본인확인용) *</label>
                    <input
                      type="text"
                      required
                      value={userPhone}
                      onChange={e => setUserPhone(e.target.value)}
                      placeholder="010-1234-5678"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">
                      외부 수신 이메일 주소 (Gmail, Naver 등) *
                    </label>
                    <input
                      type="email"
                      required
                      value={userEmail}
                      onChange={e => setUserEmail(e.target.value)}
                      placeholder="예: honggildong@gmail.com"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 font-mono text-slate-900"
                    />
                    <span className="text-[10px] text-indigo-600 font-medium mt-0.5 block">
                      * 입력하신 외부 이메일로 전자계약 서명 링크가 자동 발송됩니다.
                    </span>
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">전문 분야 / 직무</label>
                    <input
                      type="text"
                      value={position}
                      onChange={e => setPosition(e.target.value)}
                      placeholder="UI/UX 디자이너, 풀스택 개발자 등"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">주민등록번호 (직원이 서명 시 직접 작성 가능)</label>
                    <input
                      type="text"
                      maxLength={14}
                      value={residentNumber}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 13);
                        setResidentNumber(raw.length <= 6 ? raw : `${raw.slice(0, 6)}-${raw.slice(6)}`);
                      }}
                      placeholder="예: 950101-1234567"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 font-mono font-bold text-xs"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-[11px] font-bold text-slate-600 block mb-1">자택 주소 (직원이 서명 시 직접 작성 가능)</label>
                    <input
                      type="text"
                      value={userAddress}
                      onChange={e => setUserAddress(e.target.value)}
                      placeholder="예: 서울시 강서구 양천로 354 옥천빌딩"
                      className="w-full px-3 py-2 bg-white rounded-xl border border-slate-300 text-xs"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Template Selection & Contract Title */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700">3. 계약서 양식(템플릿) 선택</label>
              </div>

              <div className="flex flex-wrap gap-2">
                {contractTemplates.map(tmpl => (
                  <button
                    key={tmpl.TemplateID}
                    type="button"
                    onClick={() => handleTemplateSelect(tmpl)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                      contractType === tmpl.Type
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {tmpl.Name}
                  </button>
                ))}
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-600 block mb-1">계약서 제목</label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs font-bold rounded-xl border border-slate-300 bg-white"
                />
              </div>
            </div>

            {/* Step 4: Dates & Remuneration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-4">
              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">계약 시작일 *</label>
                <input
                  type="date"
                  required
                  value={contractStartDate}
                  onChange={e => setContractStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">계약 종료일 (미입력 시 정규직)</label>
                <input
                  type="date"
                  value={contractEndDate}
                  onChange={e => setContractEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white"
                />
              </div>

              {/* Link Expiration Option Selection */}
              <div className="md:col-span-2 bg-indigo-50/70 border border-indigo-200 rounded-xl p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-extrabold text-indigo-950 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-indigo-600" />
                    <span>서명 링크 유효기간 설정 (외부인 / 임직원 공통)</span>
                  </label>
                  <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100 px-2 py-0.5 rounded">
                    만료 후 1클릭 재연장 가능
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {[
                    { id: '7_DAYS', label: '7일 (기본)' },
                    { id: '14_DAYS', label: '14일' },
                    { id: '30_DAYS', label: '30일' },
                    { id: 'CUSTOM', label: '직접 지정' },
                    { id: 'UNLIMITED', label: '무제한 (만료 없음)' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setExpireOption(opt.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                        expireOption === opt.id
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {expireOption === 'CUSTOM' && (
                  <div className="pt-2">
                    <label className="text-[11px] font-bold text-slate-700 block mb-1">직접 지정 만료일자:</label>
                    <input
                      type="date"
                      required
                      value={customExpireDate}
                      onChange={e => setCustomExpireDate(e.target.value)}
                      className="px-3 py-1.5 text-xs rounded-xl border border-slate-300 bg-white"
                    />
                  </div>
                )}
                <p className="text-[10px] text-slate-500">
                  * 지정된 만료일시(23:59:59)가 지나면 수신자 서명이 자동 차단되며, 관리자 페이지에서 1클릭으로 7일씩 재연장할 수 있습니다.
                </p>
              </div>

              {/* Custom template compensation toggle */}
              {contractType === 'CUSTOM' && (
                <div className="md:col-span-2 bg-slate-100 p-2.5 rounded-xl">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                    <input
                      type="checkbox"
                      checked={includeCustomSalary}
                      onChange={e => setIncludeCustomSalary(e.target.checked)}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <span>본 서식에 연봉/용역 금액 및 지급 조건 포함하기</span>
                  </label>
                </div>
              )}

              {/* Conditional Remuneration inputs */}
              {isCompensationRequired && (
                <>
                  {targetType === 'EMPLOYEE' ? (
                    <div className="md:col-span-2 space-y-3 bg-white p-4 rounded-2xl border border-indigo-100 shadow-2xs">
                      {/* Top Annual & Payday Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b border-slate-100">
                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            연간 총 연봉 (₩ 세전) *
                          </label>
                          <input
                            type="number"
                            step="1000000"
                            value={annualSalary}
                            onChange={e => handleAnnualSalaryChange(e.target.value)}
                            placeholder="50000000"
                            className="w-full px-3 py-2 text-xs font-black rounded-xl border border-indigo-200 bg-indigo-50/40 font-mono text-indigo-900 focus:bg-white transition-all"
                          />
                          <span className="text-[10px] text-indigo-600 font-bold mt-1 block">
                            월 환산 급여: ₩ {monthlySalaryTotal.toLocaleString()} 원
                          </span>
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            급여 정기 지급일 *
                          </label>
                          <input
                            type="text"
                            value={salaryPayday}
                            onChange={e => setSalaryPayday(e.target.value)}
                            placeholder="매달 말일"
                            className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white"
                          />
                          <div className="flex gap-1.5 mt-1">
                            {['매달 말일', '매월 25일', '매월 10일'].map(pd => (
                              <button
                                key={pd}
                                type="button"
                                onClick={() => setSalaryPayday(pd)}
                                className="text-[9px] px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded font-medium transition-colors"
                              >
                                {pd}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Salary Items Builder Header */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                            <span>💳</span> 월급여 세부 구성 및 비과세 수당 분할
                          </span>
                          <button
                            type="button"
                            onClick={handleAddCustomSalaryItem}
                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <Plus className="w-3 h-3" />
                            수당 직접 추가
                          </button>
                        </div>

                        {/* Preset Quick Chips */}
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[10px] text-slate-400 font-bold">비과세/수당 프리셋:</span>
                          {PRESET_SALARY_ITEMS.map((preset, pIdx) => (
                            <button
                              key={pIdx}
                              type="button"
                              onClick={() => handleAddSalaryPreset(preset)}
                              className="text-[10px] font-bold px-2 py-0.5 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 border border-slate-200 rounded-md transition-colors cursor-pointer"
                            >
                              + {preset.name} ({preset.isTaxFree ? '비과세' : '과세'})
                            </button>
                          ))}
                        </div>

                        {/* Salary Items List Table */}
                        <div className="space-y-2 pt-1 max-h-60 overflow-y-auto pr-1">
                          {salaryItems.map((item, idx) => (
                            <div
                              key={item.id || idx}
                              className="grid grid-cols-12 gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl items-center text-xs"
                            >
                              {/* Item Name */}
                              <div className="col-span-4 sm:col-span-3">
                                <input
                                  type="text"
                                  value={item.name}
                                  onChange={e => handleUpdateSalaryItem(idx, 'name', e.target.value)}
                                  placeholder="항목명"
                                  className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold"
                                />
                              </div>

                              {/* Amount */}
                              <div className="col-span-5 sm:col-span-4">
                                <div className="relative flex items-center">
                                  <input
                                    type="number"
                                    step="10000"
                                    value={item.amount}
                                    onChange={e => handleUpdateSalaryItem(idx, 'amount', Number(e.target.value))}
                                    placeholder="금액 (원)"
                                    className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold text-right pr-6"
                                  />
                                  <span className="absolute right-2 text-[10px] text-slate-400 font-bold pointer-events-none">원</span>
                                </div>
                              </div>

                              {/* Tax Free Toggle */}
                              <div className="col-span-2 sm:col-span-2 flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateSalaryItem(idx, 'isTaxFree', !item.isTaxFree)}
                                  className={`px-2 py-1 rounded-md text-[10px] font-black transition-all cursor-pointer ${
                                    item.isTaxFree
                                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                      : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                  }`}
                                >
                                  {item.isTaxFree ? '🌿 비과세' : '과세'}
                                </button>
                              </div>

                              {/* Delete Button */}
                              <div className="col-span-1 sm:col-span-3 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSalaryItem(idx)}
                                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                  title="항목 삭제"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Salary Summary Badge Box */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 text-center text-xs">
                          <div className="p-2 bg-indigo-50/70 border border-indigo-100 rounded-xl">
                            <span className="text-[10px] text-slate-500 font-bold block">월 총지급액</span>
                            <span className="text-xs font-black text-indigo-700 font-mono">
                              ₩ {monthlySalaryTotal.toLocaleString()}
                            </span>
                          </div>
                          <div className="p-2 bg-emerald-50/70 border border-emerald-100 rounded-xl">
                            <span className="text-[10px] text-emerald-700 font-bold block">비과세 합계</span>
                            <span className="text-xs font-black text-emerald-800 font-mono">
                              ₩ {totalTaxFree.toLocaleString()}
                            </span>
                          </div>
                          <div className="p-2 bg-slate-100 border border-slate-200 rounded-xl">
                            <span className="text-[10px] text-slate-500 font-bold block">과세 대상액</span>
                            <span className="text-xs font-bold text-slate-800 font-mono">
                              ₩ {totalTaxable.toLocaleString()}
                            </span>
                          </div>
                          <div className="p-2 bg-amber-50/70 border border-amber-100 rounded-xl">
                            <span className="text-[10px] text-amber-700 font-bold block">연간 환산총액</span>
                            <span className="text-xs font-black text-amber-900 font-mono">
                              ₩ {(monthlySalaryTotal * 12).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="md:col-span-2 space-y-3 bg-white p-4 rounded-2xl border border-indigo-100 shadow-2xs">
                      {/* Top Tax Mode & Payment Date */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-3 border-b border-slate-100">
                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            과세 및 세무 처리 방식 *
                          </label>
                          <div className="flex gap-1.5">
                            {[
                              { id: 'INCOME_TAX_3_3', label: '3.3% 원천징수' },
                              { id: 'VAT_10', label: '10% 부가세 (세금계산서)' },
                              { id: 'TAX_EXEMPT', label: '면세 (0%)' }
                            ].map(tm => (
                              <button
                                key={tm.id}
                                type="button"
                                onClick={() => setFreelanceTaxMode(tm.id)}
                                className={`flex-1 py-1.5 px-2 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                                  freelanceTaxMode === tm.id
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                }`}
                              >
                                {tm.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="text-[11px] font-bold text-slate-700 block mb-1">
                            비용 지급일 / 정산 일정 *
                          </label>
                          <input
                            type="text"
                            value={freelancePaymentDate}
                            onChange={e => setFreelancePaymentDate(e.target.value)}
                            placeholder="예: 매월 10일, 과업 검수 완료 후 7영업일 이내"
                            className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white"
                          />
                          <div className="flex gap-1.5 mt-1 flex-wrap">
                            {['매월 10일', '매달 말일', '검수 완료 후 7일 이내'].map(pd => (
                              <button
                                key={pd}
                                type="button"
                                onClick={() => setFreelancePaymentDate(pd)}
                                className="text-[9px] px-1.5 py-0.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded font-medium transition-colors"
                              >
                                {pd}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Freelance Items Builder */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-slate-800 flex items-center gap-1.5">
                            <span>📑</span> 용역 대금 및 과업 분할 항목
                          </span>
                          <button
                            type="button"
                            onClick={handleAddFreelanceItem}
                            className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <Plus className="w-3 h-3" />
                            용역 항목 추가
                          </button>
                        </div>

                        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                          {freelanceItems.map((fItem, fIdx) => (
                            <div
                              key={fItem.id || fIdx}
                              className="grid grid-cols-12 gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-xl items-center text-xs"
                            >
                              <div className="col-span-6 sm:col-span-5">
                                <input
                                  type="text"
                                  value={fItem.name}
                                  onChange={e => handleUpdateFreelanceItem(fIdx, 'name', e.target.value)}
                                  placeholder="과업 또는 항목명"
                                  className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-bold"
                                />
                              </div>
                              <div className="col-span-5 sm:col-span-5">
                                <div className="relative flex items-center">
                                  <input
                                    type="number"
                                    step="100000"
                                    value={fItem.amount}
                                    onChange={e => handleUpdateFreelanceItem(fIdx, 'amount', Number(e.target.value))}
                                    placeholder="공급가액"
                                    className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold text-right pr-6"
                                  />
                                  <span className="absolute right-2 text-[10px] text-slate-400 font-bold pointer-events-none">원</span>
                                </div>
                              </div>
                              <div className="col-span-1 sm:col-span-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFreelanceItem(fIdx)}
                                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                  title="항목 삭제"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Freelance Summary Box */}
                        <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-xl space-y-1.5 text-xs">
                          <div className="flex justify-between items-center text-slate-700">
                            <span>총 공급가액 합계:</span>
                            <span className="font-bold font-mono text-slate-900">₩ {freelanceTotalSupply.toLocaleString()} 원</span>
                          </div>
                          {freelanceTaxMode === 'INCOME_TAX_3_3' && (
                            <div className="flex justify-between items-center text-rose-600">
                              <span>- 사업소득세 원천징수 (3.3%):</span>
                              <span className="font-mono">₩ {freelanceTax.toLocaleString()} 원</span>
                            </div>
                          )}
                          {freelanceTaxMode === 'VAT_10' && (
                            <div className="flex justify-between items-center text-indigo-700">
                              <span>+ 부가가치세 (10%):</span>
                              <span className="font-mono">₩ {freelanceTax.toLocaleString()} 원</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center pt-1.5 border-t border-indigo-100 font-black text-indigo-900 text-sm">
                            <span>
                              {freelanceTaxMode === 'INCOME_TAX_3_3' ? '최종 실지급액:' : freelanceTaxMode === 'VAT_10' ? '총 청구금액 (VAT 포함):' : '총 지급액 (면세):'}
                            </span>
                            <span className="font-mono text-indigo-700 text-base">₩ {freelanceNetOrTotal.toLocaleString()} 원</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">소정 근로시간 *</label>
                <input
                  type="text"
                  value={workingHours}
                  onChange={e => setWorkingHours(e.target.value)}
                  placeholder="예: 09:00 ~ 18:00 (휴게 12:00 ~ 13:00)"
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-[11px] font-bold text-slate-700 block mb-1">근무(용역수행) 장소 *</label>
                <input
                  type="text"
                  value={workingPlace}
                  onChange={e => setWorkingPlace(e.target.value)}
                  placeholder="예: 서울시 강서구 양천로 354 클로우플레이크 본사"
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-[11px] font-bold text-slate-700 block mb-1">담당 업무 / 과업 설명 *</label>
                <textarea
                  rows={2}
                  value={jobDescription}
                  onChange={e => setJobDescription(e.target.value)}
                  placeholder="담당할 주요 직무 및 과업 내용을 기록해 주세요."
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 bg-white resize-none font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
            </div>

            {/* Step 4-2: Standard Clauses Editor */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                  4. 기본 계약 조항 및 서약사항 (직접 수정 가능)
                </label>
              </div>
              <div className="space-y-2.5">
                {standardClauses.map((clause, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5 text-left">
                    <div className="font-bold text-indigo-900 text-[11px]">
                      {clause.title}
                    </div>
                    <textarea
                      rows={2}
                      value={clause.content}
                      onChange={e => {
                        const val = e.target.value;
                        setStandardClauses(prev => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], content: val };
                          return next;
                        });
                      }}
                      className="w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-2 resize-none focus:ring-2 focus:ring-indigo-500 focus:outline-hidden"
                      required
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Step 5: Custom Clauses Builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  5. 특약 조항 블록 빌더 ({customClauses.length}개)
                </label>
                <button
                  type="button"
                  onClick={handleAddEmptyClause}
                  className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-bold hover:bg-indigo-100 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  새 조항 추가
                </button>
              </div>

              {/* Preset Quick Chips */}
              <div className="space-y-1">
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                  추천 특약 프리셋 원클릭 추가:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_CLAUSES.map((preset, pIdx) => (
                    <button
                      key={pIdx}
                      type="button"
                      onClick={() => handleAddPresetClause(preset)}
                      className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-[10px] font-medium transition-colors"
                    >
                      + {preset.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Clause List */}
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {customClauses.map((clause, idx) => (
                  <div key={idx} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5 relative group">
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={clause.title}
                        onChange={e => handleUpdateClause(idx, 'title', e.target.value)}
                        placeholder="특약 조항 제목"
                        className="text-xs font-bold text-indigo-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:outline-hidden w-2/3"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveClause(idx)}
                        className="text-slate-400 hover:text-rose-600 p-1 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <textarea
                      rows={2}
                      value={clause.content}
                      onChange={e => handleUpdateClause(idx, 'content', e.target.value)}
                      placeholder="특약 조항 상세 내용"
                      className="w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-2 resize-none focus:outline-hidden focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Step 5-2: Signature & Seal Preview */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
              <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <BookmarkCheck className="w-4 h-4 text-indigo-600" />
                6. 계약 당사자 서명 및 법인 날인 확인 (미리보기)
              </span>
              <div className="grid grid-cols-2 gap-3 text-left">
                {/* 회사 서명 부분 */}
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-[11px] space-y-1 relative">
                  <span className="text-[9px] font-black text-slate-400 block mb-1">[회사 (사용자)]</span>
                  <div><strong>상호:</strong> {companySeal?.companyName || '주식회사 클로우플레이크'}</div>
                  <div><strong>대표자:</strong> {companySeal?.representativeName || '대표이사 이용관'}</div>
                  <div><strong>주소:</strong> {companySeal?.address || '서울시 강서구 양천로 354 옥천빌딩 9층 클로우플레이크 본사'}</div>
                  
                  {/* 도장/직인 표시 */}
                  <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between min-h-[40px]">
                    <span className="text-[10px] text-slate-500 font-bold">법인 직인:</span>
                    {(companySeal?.imageUrl || '/clouflake.png') ? (
                      <img src={companySeal?.imageUrl || '/clouflake.png'} alt="직인" className="h-10 object-contain rotate-[-4deg]" />
                    ) : companySeal?.shape === 'OVAL' ? (
                      <div className="w-12 h-10 rounded-[40%] border border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[9px] text-center bg-rose-50/40 rotate-[-4deg] whitespace-pre-line leading-none p-1">
                        {companySeal.sealText}
                      </div>
                    ) : companySeal?.shape === 'SQUARE' ? (
                      <div className="w-10 h-10 border border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[9px] text-center bg-rose-50/40 rotate-[-3deg] whitespace-pre-line leading-none p-1">
                        {companySeal.sealText}
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full border border-rose-600 flex items-center justify-center text-rose-600 font-bold text-[9px] text-center bg-rose-50/40 rotate-[-6deg] whitespace-pre-line leading-none p-1">
                        {companySeal?.sealText || '클로우\n플레이크\n대표이사'}
                      </div>
                    )}
                  </div>
                </div>

                {/* 직원 서명 부분 */}
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-[11px] space-y-1">
                  <span className="text-[9px] font-black text-slate-400 block mb-1">
                    [{targetType === 'FREELANCER' ? '수급인 (프리랜서)' : '근로자'}]
                  </span>
                  <div><strong>성명:</strong> {userName || '(계약 대상자 이름 없음)'}</div>
                  <div><strong>주민번호:</strong> {residentNumber ? <span className="font-mono font-bold text-slate-800">{residentNumber}</span> : <span className="text-amber-700 font-semibold bg-amber-50 px-1 py-0.2 rounded border border-amber-200">서명 시 직원 직접 기재</span>}</div>
                  <div><strong>자택주소:</strong> {userAddress ? <span className="font-semibold text-slate-800">{userAddress}</span> : <span className="text-amber-700 font-semibold bg-amber-50 px-1 py-0.2 rounded border border-amber-200">서명 시 직원 직접 기재</span>}</div>
                  <div><strong>연락처:</strong> {userPhone || '(연락처 없음)'}</div>
                  <div><strong>이메일:</strong> {userEmail || '(이메일 없음)'}</div>
                  <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between min-h-[40px]">
                    <span className="text-[10px] text-slate-500 font-bold">서명 상태:</span>
                    <span className="text-[9px] text-amber-600 font-bold bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      전송 후 서명 대기
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 6: Save as Template Option */}
            <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-3.5 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsTemplate}
                  onChange={e => setSaveAsTemplate(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded border-slate-300"
                />
                <span className="text-xs font-bold text-indigo-950 flex items-center gap-1.5">
                  <BookmarkCheck className="w-3.5 h-3.5 text-indigo-600" />
                  현재 작성한 조항 구성을 사내 '새 템플릿'으로 저장하기
                </span>
              </label>

              {saveAsTemplate && (
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={e => setNewTemplateName(e.target.value)}
                  placeholder="저장할 템플릿 이름 (예: AI개발팀 맞춤 근로계약서)"
                  className="w-full text-xs px-3 py-2 rounded-lg border border-indigo-200 bg-white font-bold"
                />
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-3 border-t border-slate-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition-all active:scale-98 cursor-pointer"
              >
                <Send className="w-4 h-4" />
                {isSubmitting ? '발송 처리 중...' : '계약서 생성 및 서명 요청 발송'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContractCreateModal;
